import { useMemo } from 'react';
import { useSeoFetch } from '@/hooks/useSeoFetch';
import useSeoProject from '@/hooks/useSeoProject';
import seoInternalLinksService from '@/services/seoInternalLinks';
import seoKeywordsService from '@/services/seoKeywords';
import { FetchApiIds } from '@/types';
import { SeoInternalLink, SeoTrackedKeyword } from '@/types/api/seo';
import SeoInternalLinksComponent from './SeoInternalLinks.component';

type KeywordsPaginated = { data: SeoTrackedKeyword[]; count: number; currentPage: number; lastPage: number; nextPage: number | null; prevPage: number | null };

const SeoInternalLinksPage = function (): JSX.Element {
  const { project } = useSeoProject();
  const projectId = project?.id ?? '';

  const { data: linkStats, isLoading: isLoadingStats } = useSeoFetch(
    ['seo-link-stats', projectId],
    () => seoInternalLinksService.getLinkStats(projectId),
    { enabled: !!projectId }
  );

  const { data: links = [], isLoading: isLoadingLinks } = useSeoFetch<SeoInternalLink[]>(
    ['seo-link-graph', projectId],
    () => seoInternalLinksService.getLinkGraph(projectId),
    { enabled: !!projectId }
  );

  const { data: keywordsData } = useSeoFetch<KeywordsPaginated>(
    [FetchApiIds.SEO_KEYWORDS, projectId],
    () => seoKeywordsService.getAll({ projectId, page: 1, take: 500 }),
    { enabled: !!projectId }
  );

  const keywords = keywordsData?.data ?? [];

  // Find keywords whose ranking URL has few inbound links
  const linkOpportunities = useMemo(() => {
    if (!linkStats || keywords.length === 0) return [];

    const inboundCounts = new Map<string, number>();
    for (const link of links) {
      inboundCounts.set(link.targetUrl, (inboundCounts.get(link.targetUrl) ?? 0) + 1);
    }

    return keywords
      .filter((kw) => kw.latestSnapshot?.url)
      .map((kw) => {
        const url = kw.latestSnapshot!.url!;
        const inbound = inboundCounts.get(url) ?? 0;
        return {
          keyword: kw.keyword,
          url,
          position: kw.latestSnapshot?.position ?? null,
          impressions: kw.latestSnapshot?.searchVolume ?? 0,
          inboundLinks: inbound,
        };
      })
      .filter((o) => o.inboundLinks < 3)
      .sort((a, b) => (b.impressions ?? 0) - (a.impressions ?? 0));
  }, [linkStats, keywords, links]);

  return (
    <SeoInternalLinksComponent
      stats={linkStats ?? null}
      links={links}
      linkOpportunities={linkOpportunities}
      isLoading={isLoadingStats || isLoadingLinks}
    />
  );
};

export default SeoInternalLinksPage;
